import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Contact, Users, PhoneCall, Mail, BellRing, Search, GitMerge, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { HelpButton } from '../../components/help/HelpButton';
import { cardHelp } from '../../config/helpContent';
import { useClients, EMPTY_CLIENT_FILTERS, type ClientCareFilters } from '../../hooks/useClients';
import { useLoanBooks } from '../../hooks/useLoanBooks';
import { useAuth } from '../../hooks/useAuth';
import { ClientDetailDrawer } from '../../components/clients/ClientDetailDrawer';
import { ClientIdentityModal } from '../../components/clients/ClientIdentityModal';
import { ClientCareWhoToCallPanel } from '../../components/clients/ClientCareWhoToCallPanel';
// eDM client tags (plan-v3 core, Slice E3): every tag control below renders
// only when edm_content is on AND the role may tag clients.
import { canTagClients, useEntityTags, useTagSelection } from '../../hooks/useEntityTags';
import { edmErrorMessage, isEdmContentEnabled } from '../../utils/edm';
import BulkTagBar from '../leads/edm/components/BulkTagBar';
import EntityTagCell from '../../components/tags/EntityTagCell';
import EntityTagFilter from '../../components/tags/EntityTagFilter';

/** eDM bulk-tag notice nouns. */
const CLIENT_NOUN = { one: 'client', many: 'clients' };

/** Identity tooling (merge/split) is an admin act — gate on the role claim. */
const IDENTITY_ADMIN_ROLES = ['super_admin', 'administrator'];

const PAGE_SIZE = 50;

function fmtDate(v: unknown): string {
  if (!v) return '—';
  let ms: number | null = null;
  if (typeof v === 'object' && v !== null && typeof (v as { _seconds?: number })._seconds === 'number') {
    ms = (v as { _seconds: number })._seconds * 1000;
  } else if (typeof v === 'string') {
    ms = Date.parse(v);
  } else if (typeof v === 'number') {
    ms = v;
  }
  if (ms == null || Number.isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** A compact, dark-theme single-select used by the filter bar. */
function FilterSelect({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-500 min-w-[10rem]"
      >
        {children}
      </select>
    </label>
  );
}

export default function ClientDatabasePage() {
  const { userData, companyData } = useOutletContext<{
    userData: { companyId?: string } | null;
    companyData?: { featureFlags?: Record<string, unknown> | null } | null;
  }>();
  const { claims } = useAuth();
  const companyId = userData?.companyId;
  const isIdentityAdmin = !!claims?.role && IDENTITY_ADMIN_ROLES.includes(claims.role);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ClientCareFilters>(EMPTY_CLIENT_FILTERS);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [identityModal, setIdentityModal] = useState<{ mode: 'merge' | 'split'; clientId: string | null } | null>(null);

  /** Any filter change resets to page 1 so the user never lands on an empty page. */
  const updateFilter = (patch: Partial<ClientCareFilters>) => {
    setPage(1);
    setFilters((f) => ({ ...f, ...patch }));
  };

  const { clients, total, summary, facets, hasAccess, loading, error, refetch } = useClients({
    companyId,
    page,
    pageSize: PAGE_SIZE,
    search,
    filters,
  });
  const { enabledLoanBooks } = useLoanBooks({ companyId });

  // entityCode → friendly loan-book name (facets carry the code; names come from getLoanBooks).
  const loanBookName = useMemo(() => {
    const map = new Map<string, string>();
    for (const lb of enabledLoanBooks) if (lb.entityCode) map.set(lb.entityCode, lb.name);
    return map;
  }, [enabledLoanBooks]);

  // ---- eDM client tags (edm_content). The server (edmTagAction, listClients
  // tagNorms) is the authority; this only hides what it would refuse.
  const tagsOn = isEdmContentEnabled(companyData?.featureFlags) && canTagClients(claims?.role);
  const visibleClientIds = useMemo(() => clients.map((c) => c.clientId), [clients]);
  const clientTags = useEntityTags({ companyId, kind: 'client', enabled: tagsOn, ids: visibleClientIds });
  // A tag change can move rows in or out of the active tag filter.
  const tagFilterOn = (filters.tagNorms?.length ?? 0) > 0;
  const onTagsApplied = useCallback(() => {
    if (tagFilterOn) void refetch();
  }, [tagFilterOn, refetch]);
  // Rows the server reported as outside the caller's tagging scope are not selectable.
  const taggableClientIds = useMemo(
    () => visibleClientIds.filter((id) => clientTags.tagsById[id] !== null),
    [visibleClientIds, clientTags.tagsById],
  );
  const tagSel = useTagSelection(taggableClientIds, clientTags.bulkTag, CLIENT_NOUN, onTagsApplied);
  const tagColumns = tagsOn ? 2 : 0;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  // A tag removal under an active tag filter can shrink the result below the
  // current page; step back to the last page (tag mode only, so the flag-off
  // page behaves exactly as before).
  useEffect(() => {
    if (tagsOn && tagFilterOn && !loading && page > totalPages) setPage(totalPages);
  }, [tagsOn, tagFilterOn, loading, page, totalPages]);
  const headers = ['Client', 'Broker', 'Office', 'Loans', 'Settled', 'Last Contact', 'Status'];

  // Per-tenant: only surface a dimension when there's more than one value to pick.
  const showOfficeFilter = facets.offices.length > 1;
  const showBrokerFilter = facets.brokers.length > 1;
  const showLoanBookFilter = facets.loanBooks.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Contact}
        title="Client Care"
        subtitle="Manage your clients — never miss a moment worth a call."
        badge="Preview"
        onRefresh={refetch}
        isRefreshing={loading}
        actions={
          <>
            <HelpButton pageKey="clientCare" />
            {isIdentityAdmin ? (
              <button
                onClick={() => setIdentityModal({ mode: 'merge', clientId: null })}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm font-semibold text-gray-200 hover:bg-gray-700 hover:text-white transition-colors"
              >
                <GitMerge className="w-4 h-4" /> Resolve identity
              </button>
            ) : null}
          </>
        }
      />

      {!hasAccess ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center text-gray-400">
          <Contact className="w-8 h-8 mx-auto mb-3 text-gray-600" />
          <p className="text-white font-medium">You're not linked to a broker yet</p>
          <p className="text-sm mt-1">
            Client Care appears here once you're assigned to a broker. Ask your administrator to link your account.
          </p>
        </div>
      ) : (
        <>
          <ClientCareWhoToCallPanel
            companyId={companyId}
            onSelectClient={setSelectedClientId}
            officeId={filters.officeId}
            brokerDisplay={filters.brokerDisplay}
            loanBookEntityCode={filters.loanBookEntityCode}
            status={filters.status}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total Clients" value={summary.total} icon={<Users className="w-5 h-5" />} gradientColor="#3b82f6" subtitle="In the current view" helpKey="totalClients" helpTooltip={cardHelp('clientCare', 'totalClients')} />
            <StatCard title="Active" value={summary.activeCount} icon={<PhoneCall className="w-5 h-5" />} gradientColor="#22c55e" helpKey="activeClients" helpTooltip={cardHelp('clientCare', 'activeClients')} />
            <StatCard title="With email" value={summary.withEmailCount} icon={<Mail className="w-5 h-5" />} gradientColor="#f59e0b" helpKey="withEmail" helpTooltip={cardHelp('clientCare', 'withEmail')} />
            <StatCard title="Overdue to call" value={summary.overdueCount} icon={<BellRing className="w-5 h-5" />} gradientColor="#ef4444" helpKey="overdueToCall" helpTooltip={cardHelp('clientCare', 'overdueToCall')} />
          </div>

          {/* Filter bar: search + single-select dropdowns. */}
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 flex flex-wrap gap-3 items-end">
            <label className="flex flex-col gap-1 flex-1 min-w-[14rem]">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Search</span>
              <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2">
                <Search className="w-4 h-4 text-gray-500" />
                <input
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  placeholder="Search clients by name…"
                  className="bg-transparent outline-none w-full text-sm text-white placeholder-gray-500"
                />
              </div>
            </label>

            {showOfficeFilter && (
              <FilterSelect
                label="Office"
                value={filters.officeId ?? ''}
                onChange={(v) => updateFilter({ officeId: v || null })}
              >
                <option value="">All offices</option>
                {facets.offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.count})
                  </option>
                ))}
              </FilterSelect>
            )}

            {showBrokerFilter && (
              <FilterSelect
                label="Broker"
                value={filters.brokerDisplay ?? ''}
                onChange={(v) => updateFilter({ brokerDisplay: v || null })}
              >
                <option value="">All brokers</option>
                {facets.brokers.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.value} ({b.count})
                  </option>
                ))}
              </FilterSelect>
            )}

            {showLoanBookFilter && (
              <FilterSelect
                label="Loan book"
                value={filters.loanBookEntityCode ?? ''}
                onChange={(v) => updateFilter({ loanBookEntityCode: v || null })}
              >
                <option value="">All loan books</option>
                {facets.loanBooks.map((lb) => (
                  <option key={lb.entityCode} value={lb.entityCode}>
                    {loanBookName.get(lb.entityCode) ?? lb.entityCode} ({lb.count})
                  </option>
                ))}
              </FilterSelect>
            )}

            <FilterSelect
              label="Status"
              value={filters.status ?? ''}
              onChange={(v) => updateFilter({ status: v === 'active' || v === 'inactive' ? v : null })}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </FilterSelect>

            <FilterSelect
              label="Call status"
              value={filters.overdueOnly ? 'overdue' : ''}
              onChange={(v) => updateFilter({ overdueOnly: v === 'overdue' })}
            >
              <option value="">All clients</option>
              <option value="overdue">Overdue to call</option>
            </FilterSelect>

            {tagsOn && (
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Tag</span>
                <EntityTagFilter
                  registry={clientTags.registry}
                  registryError={clientTags.registryError}
                  onRetry={clientTags.retry}
                  value={filters.tagNorms?.[0] ?? null}
                  onChange={(t) => updateFilter({ tagNorms: t ? [t] : null })}
                  className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-500 min-w-[10rem]"
                />
              </div>
            )}
          </div>

          {tagsOn && (
            <BulkTagBar
              selectedCount={tagSel.selected.size}
              tags={clientTags.registry ?? []}
              busy={tagSel.busy}
              onApply={(add, remove) => void tagSel.apply(add, remove)}
              onClear={tagSel.clear}
            />
          )}
          {tagsOn && tagSel.notice && (
            <div role="status" className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-200">
              {tagSel.notice}
            </div>
          )}
          {tagsOn && clientTags.tagsError ? (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              Tags could not be loaded. {edmErrorMessage(clientTags.tagsError)}{' '}
              <button type="button" className="underline" onClick={clientTags.retry}>
                Retry
              </button>
            </div>
          ) : null}
          {tagsOn && tagSel.error && (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {tagSel.error}
            </div>
          )}

          {error && (
            <div className="bg-red-900/30 border border-red-500/30 text-red-300 rounded-lg p-3 text-sm">{error}</div>
          )}

          <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  {tagsOn && (
                    <th className="px-6 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={tagSel.allSelected}
                        onChange={tagSel.toggleAll}
                        aria-label="Select all clients on this page"
                        className="accent-blue-500"
                      />
                    </th>
                  )}
                  {headers.map((h) => (
                    <th key={h} className="px-6 py-4 text-xs font-semibold text-gray-300 uppercase tracking-wider text-left">
                      {h}
                    </th>
                  ))}
                  {tagsOn && (
                    <th className="px-6 py-4 text-xs font-semibold text-gray-300 uppercase tracking-wider text-left">
                      Tags
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading && clients.length === 0 ? (
                  <tr>
                    <td colSpan={headers.length + tagColumns} className="px-6 py-8 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : clients.length === 0 ? (
                  <tr>
                    <td colSpan={headers.length + tagColumns} className="px-6 py-8 text-center text-gray-400">
                      No clients found.
                    </td>
                  </tr>
                ) : (
                  clients.map((c) => (
                    <tr
                      key={c.clientId}
                      onClick={() => setSelectedClientId(c.clientId)}
                      className="hover:bg-gray-700 transition-colors cursor-pointer"
                    >
                      {tagsOn && (
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={tagSel.selected.has(c.clientId)}
                            onChange={() => tagSel.toggle(c.clientId)}
                            disabled={clientTags.tagsById[c.clientId] === null}
                            title={clientTags.tagsById[c.clientId] === null ? 'You cannot tag this client' : undefined}
                            aria-label={`Select ${c.displayName}`}
                            className="accent-blue-500"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{c.displayName}</span>
                          {c.ambiguousIdentity && (
                            <button
                              onClick={
                                isIdentityAdmin
                                  ? (e) => {
                                      e.stopPropagation();
                                      setIdentityModal({ mode: 'split', clientId: c.clientId });
                                    }
                                  : undefined
                              }
                              disabled={!isIdentityAdmin}
                              title={
                                isIdentityAdmin
                                  ? 'Possible two people on one record — click to split'
                                  : 'Possible two people on one record'
                              }
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-amber-500/15 text-amber-300 border-amber-500/30 ${
                                isIdentityAdmin ? 'hover:bg-amber-500/25 cursor-pointer' : 'cursor-default'
                              }`}
                            >
                              <AlertTriangle className="w-3 h-3" /> Ambiguous
                            </button>
                          )}
                        </div>
                        {c.primaryContactName ? (
                          <div className="text-xs text-gray-400">c/o {c.primaryContactName}</div>
                        ) : c.email ? (
                          <div className="text-xs text-gray-500">{c.email}</div>
                        ) : null}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{c.primaryBrokerDisplay ?? '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{c.officeName ?? c.officeId ?? '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-300">{c.loanCount}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{c.earliestSettlementDate ?? 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-400">{fmtDate(c.lastContactAt)}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${
                            c.hasActiveLoan
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-gray-700/40 text-gray-400 border-gray-600/40'
                          }`}
                        >
                          {c.hasActiveLoan ? 'Active' : c.status}
                        </span>
                      </td>
                      {tagsOn && (
                        <td className="px-6 py-4">
                          <EntityTagCell entityId={c.clientId} tags={clientTags.tagsById[c.clientId]} />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-400">
              <span>
                Page {page} of {totalPages} · {total} clients
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded border border-gray-600 disabled:opacity-40 hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 rounded border border-gray-600 disabled:opacity-40 hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ClientDetailDrawer
        companyId={companyId}
        clientId={selectedClientId}
        onClose={() => setSelectedClientId(null)}
        onChanged={refetch}
        onOpenClient={setSelectedClientId}
      />

      {isIdentityAdmin && identityModal && (
        <ClientIdentityModal
          companyId={companyId}
          clients={clients}
          initialMode={identityModal.mode}
          initialClientId={identityModal.clientId}
          onClose={() => setIdentityModal(null)}
          onChanged={refetch}
        />
      )}
    </div>
  );
}
